import { useState } from 'react';

interface VideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function VideoUploadModal({ isOpen, onClose }: VideoUploadModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'funny',
    videoUrl: '',
    uploaderName: '',
    thumbnailUrl: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isOpen) return null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Video title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }
    
    if (!formData.videoUrl.trim()) {
      newErrors.videoUrl = 'Video URL is required';
    } else if (!formData.videoUrl.includes('youtube.com') && !formData.videoUrl.includes('youtu.be')) {
      newErrors.videoUrl = 'Please provide a valid YouTube URL';
    }
    
    if (!formData.uploaderName.trim()) {
      newErrors.uploaderName = 'Your name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSubmit = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d6dm1tatehdqnvnpj0qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSubmit as any).toString()
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => {
          setSubmitSuccess(false);
          setFormData({
            title: '',
            description: '',
            category: 'funny',
            videoUrl: '',
            uploaderName: '',
            thumbnailUrl: ''
          });
          onClose();
        }, 2500);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-8 py-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Upload Your Cat Video</h2>
              <p className="text-teal-50 text-sm mt-1">Share your furry friend with the world!</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="mx-8 mt-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-green-500 text-2xl"></i>
            <div>
              <p className="font-semibold text-green-900">Upload Successful!</p>
              <p className="text-sm text-green-700">Your video will be reviewed and published soon.</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-8" data-readdy-form id="cat-video-upload-form">
          {/* Guidelines */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <i className="ri-information-line"></i>
              Upload Guidelines
            </h3>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• Cat content only - no other animals as main subject</li>
              <li>• Family-friendly content - no violence or inappropriate material</li>
              <li>• YouTube links only - we'll embed your video</li>
              <li>• Original content or properly credited videos</li>
              <li>• Videos will be reviewed before publishing</li>
            </ul>
          </div>

          {/* Video Title */}
          <div className="mb-5">
            <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
              Video Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`w-full px-4 py-3 border ${errors.title ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
              placeholder="e.g., My Cat's Amazing Jump"
              maxLength={100}
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
          </div>

          {/* Description */}
          <div className="mb-5">
            <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className={`w-full px-4 py-3 border ${errors.description ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none`}
              placeholder="Tell us about your video... What makes it special?"
              maxLength={500}
            />
            <div className="flex items-center justify-between mt-1">
              {errors.description && <p className="text-red-500 text-xs">{errors.description}</p>}
              <p className="text-xs text-gray-500 ml-auto">{formData.description.length}/500</p>
            </div>
          </div>

          {/* Category */}
          <div className="mb-5">
            <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="funny">Funny</option>
              <option value="cute">Cute</option>
              <option value="playful">Playful</option>
              <option value="sleepy">Sleepy</option>
              <option value="wild-cats">Wild Cats</option>
            </select>
          </div>

          {/* Video URL */}
          <div className="mb-5">
            <label htmlFor="videoUrl" className="block text-sm font-semibold text-gray-700 mb-2">
              YouTube Video URL *
            </label>
            <input
              type="url"
              id="videoUrl"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleChange}
              className={`w-full px-4 py-3 border ${errors.videoUrl ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {errors.videoUrl && <p className="text-red-500 text-xs mt-1">{errors.videoUrl}</p>}
          </div>

          {/* Uploader Name */}
          <div className="mb-5">
            <label htmlFor="uploaderName" className="block text-sm font-semibold text-gray-700 mb-2">
              Your Name *
            </label>
            <input
              type="text"
              id="uploaderName"
              name="uploaderName"
              value={formData.uploaderName}
              onChange={handleChange}
              className={`w-full px-4 py-3 border ${errors.uploaderName ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm`}
              placeholder="How should we credit you?"
              maxLength={50}
            />
            {errors.uploaderName && <p className="text-red-500 text-xs mt-1">{errors.uploaderName}</p>}
          </div>

          {/* Thumbnail URL (Optional) */}
          <div className="mb-6">
            <label htmlFor="thumbnailUrl" className="block text-sm font-semibold text-gray-700 mb-2">
              Custom Thumbnail URL (Optional)
            </label>
            <input
              type="url"
              id="thumbnailUrl"
              name="thumbnailUrl"
              value={formData.thumbnailUrl}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              placeholder="https://... (leave blank to use YouTube thumbnail)"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg hover:from-teal-600 hover:to-cyan-600 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-upload-cloud-line"></i>
                  Upload Video
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}